import React, { useState, useMemo, useCallback, useContext } from "react";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import Listing from "src/components/Common/Listing";
import CodeEditorView from "../CodeEditorView";
import {
  NO_OUTPUT_PLACEHOLDER,
  QUESTIONS,
  QUIZ_DISPLAY_SECTIONS,
  TEST_CASE_HEADER_PLACEHOLDER,
  TEST_CASE_STATUS,
  TEST_CASE_TABLE_HEADER,
} from "../../constant";
import ChatAnswer from "./ChatAnswer";
import { AudioPlayer } from "../AudioPlayer";
import {
  capitalizeTitle,
  getCompanyName,
  roundOff,
} from "src/utils/helperFunctions";
import { DEFAULT_COMPANY_NAME } from "src/constants/globalConstants";
import ScorePills from "../ScorePills";
import { GetMediaComponent } from "../utils";
import { ReportPdfContext } from "src/utils/contexts";
import ScoreBreakdownModal from "../ScoreBreakdownModal";

const ProgrammingSection = (props) => {
  const { reportPdfView } = useContext(ReportPdfContext);

  const { data, plagiarismConfig } = props;
  const [isAttempted, setIsAttempted] = useState(true)
  const [displayTestCases, setDisplayTestCases] = useState(
    reportPdfView ? true : false
  );

  const [orgNameFromUrl, windowUrl] = getCompanyName();
  const isWhiteLabelledOrg = orgNameFromUrl !== DEFAULT_COMPANY_NAME;

  // Hide remarks if whitelabelled orgName -> Check from url
  const remarks =
    !isWhiteLabelledOrg && data?.score?.remark ? (
      <div className="question-summary">
        <div className="flex items-center justify-between">
          <h2>REMARKS</h2>
        </div>
        <p>{data?.score?.remark}</p>
      </div>
    ) : (
      <></>
    );

  // Judge0 returns stdout/stderr/compile_output base64 encoded
  const decodeJudgeOutput = (value) => {
    if (!value) return "";
    try {
      return atob(value).trim();
    } catch (err) {
      // Already plain text on older submissions
      return String(value).trim();
    }
  };

  // Prefer stdout, fall back to the failure reason so a blank cell is never shown
  const getActualOutput = (sub) => {
    const stdout = decodeJudgeOutput(sub?.stdout);
    if (stdout) return stdout;

    const compileOutput = decodeJudgeOutput(sub?.compile_output);
    if (compileOutput) return compileOutput;

    const stderr = decodeJudgeOutput(sub?.stderr);
    if (stderr) return stderr;

    return sub?.status?.description || NO_OUTPUT_PLACEHOLDER;
  };

  // Extract time, memory and test-cases results for a coding submission
  const getCodeResults = (data) => {
    const testCasesResults = [];
    let programHeader = TEST_CASE_HEADER_PLACEHOLDER,
      sourceCode = "";
    if (data.submission) {
      const {
        submission: { testcasesPassed, testcasesTotal, testcaseResults, rawData },
      } = data;
      const testSubmissions = rawData && rawData.submissions;

      if (testSubmissions?.length) {
        let totalAverageTime = 0,
          totalAverageMemory = 0,
          language = null,
          testCasesCount = null;

        testSubmissions.forEach((sub, index) => {
          totalAverageTime += parseFloat(sub.time);
          totalAverageMemory += sub?.memory;
          const checkTestcaseFailStatus =
            testcaseResults[index].status === TEST_CASE_STATUS.FAIL;
          const testCaseResult = {
            input: testcaseResults[index].input,
            expectedOutput: testcaseResults[index].output,
            actualOutput: getActualOutput(sub),
            result: checkTestcaseFailStatus ? (
              <CloseCircleOutlined />
            ) : (
              <CheckCircleOutlined />
            ),
            time: sub.time,
            memory: sub.memory,
            score: checkTestcaseFailStatus ? 0 : testcaseResults[index].points,
          };
          testCasesResults.push(testCaseResult);
        });

        totalAverageTime = `${(totalAverageTime / testSubmissions.length).toFixed(
          5
        )} sec`;
        totalAverageMemory = `${Math.round(
          totalAverageMemory / testSubmissions?.length
        )} KB`;
        language = `${testSubmissions[0].language.name}`;
        testCasesCount = `${testcasesPassed}/${testcasesTotal} Correct`;
        const headerData = [
          totalAverageTime,
          totalAverageMemory,
          language,
          testCasesCount,
        ];

        programHeader = programHeader.map((elem, index) => ({
          ...elem,
          value: headerData[index],
        }));
        sourceCode = testSubmissions[0].source_code;
      }
    } else {
      setIsAttempted(false)
    }
    return [programHeader, testCasesResults, sourceCode];
  };

  const [programHeader, testCasesResults, sourceCode] = useMemo(
    () => getCodeResults(data),
    [data]
  );

  // To be passed to ScorePills
  const customTextObject = data?.answer?.customText;
  const answerScore = Array.isArray(data?.testcases)
    ? data?.score
    : data?.answer?.score || 0;

  return (
    <div className="program flex flex-col">
      <ScorePills
        detailedScoreObject={customTextObject}
        score={answerScore}
        contextAnalysis={customTextObject?.contextAnalysis ?? {}}
        plagiarismReport={data.plagiarismReport}
        plagiarismConfig={plagiarismConfig}
        plagiarismChecked={data.plagiarismChecked}
      />
      {
        !isAttempted && nonAttemptedMsg
      }
      <div
        className={`flex items-center justify-between mt-10 program-header ${displayTestCases ? "w80" : "w90"
          }`}
      >
        {programHeader.map((elem, index) => (
          <div key={elem.heading} className="flex items-end">
            {index !== 0 && <div className="vertical-spacer big mr-10"></div>}
            <div className="flex flex-col">
              <p className="program-header-heading">
                {elem.heading}
                <span
                  className={`program-content-toggle ml-12 ${displayTestCases ? "hidden" : ""
                    }`}
                  onClick={() => setDisplayTestCases((prev) => !prev)}
                >
                  {programHeader.length - 1 === index
                    ? "See All Test Cases"
                    : ""}
                </span>
              </p>
              <p className="program-header-value mt-6">{elem.value}</p>
            </div>
          </div>
        ))}
      </div>
      <div
        className={`program-content ${displayTestCases ? "mt-30" : "hidden"}`}
      >
        <Listing
          dataSource={testCasesResults}
          headerLayout={"3.5fr 1.2fr 1.2fr 0.8fr 1fr 1.2fr 0.5fr"}
          headerClassName={"test-cases-header"}
          rowClassName={"test-cases-row"}
          dataLayout={"3.5fr 1.2fr 1.2fr 0.8fr 1fr 1.2fr 0.5fr"}
          headers={TEST_CASE_TABLE_HEADER}
          dataKeys={[
            {
              name: "input",
              render: (data) => {
                return (
                  // Add key here `${id}-input` when integrating Programming API
                  <div className="input test-case-io" title={data.input}>
                    {data.input}
                  </div>
                );
              },
            },
            {
              name: "expectedOutput",
              render: (data) => {
                return (
                  <div
                    className="expected-output test-case-io"
                    title={data.expectedOutput}
                  >
                    {data.expectedOutput}
                  </div>
                );
              },
            },
            {
              name: "actualOutput",
              render: (data) => {
                return (
                  <div
                    className="actual-output test-case-io"
                    title={data.actualOutput}
                  >
                    {data.actualOutput}
                  </div>
                );
              },
            },
            {
              name: "result",
              render: (data) => {
                return (
                  // Add key here `${id}-result` when integrating Programming API
                  <div className="result">{data.result}</div>
                );
              },
            },
            {
              name: "time",
              render: (data) => {
                return (
                  // Add key here `${id}-time` when integrating Programming API
                  <div className="time">{data.time}</div>
                );
              },
            },
            {
              name: "memory",
              render: (data) => {
                return (
                  // Add key here `${id}-memory` when integrating Programming API
                  <div className="memory">{data.memory}</div>
                );
              },
            },
            {
              name: "score",
              render: (data) => {
                return (
                  // Add key here `${id}-score` when integrating Programming API
                  <div className="score">{data.score}</div>
                );
              },
            },
          ]}
        />
      </div>
      <div className="editor-content flex flex-col">
        <p className="editor-header mt-50 mb-20">
          <span>{`</>`}</span> Source Code
        </p>
        <CodeEditorView code={sourceCode} />
      </div>
      {remarks}
    </div>
  );
};

const McqSection = (props) => {
  // Blank Indices keeping track of sentence completion blanks.
  const { data } = props;
  const { answer, options, question } = data;
  const checkOptionLengthWrapper = (optionLength) =>
    options.some((option) => option?.text?.length > optionLength);
  const convertOptionListingToChar = (index) =>
    `${String.fromCharCode(index + 1 + 64).toUpperCase()}.`;
  const checkOptionLength = options?.length > 4 || checkOptionLengthWrapper(30);

  const extractMediaDataForOption = useCallback(
    (option) => {
      const { type, medium } = option ?? {};
      const medialink = medium?.link;

      const audioSrc = type === "audio" ? medialink : null;
      const videoSrc = type === "video" ? medialink : null;
      const imageSrc = type === "image" ? medialink : null;

      return [audioSrc, videoSrc, imageSrc, medialink];
    },
    [answer]
  );

  const isSpeechQuestion = question.type.includes(QUESTIONS.SPEECH);
  const isSentenceCompletion = question.type.includes(
    QUESTIONS.SENTENCE_COMPLETION
  );
  const isTypingText = question.type.includes(QUESTIONS.TYPING_TEXT);
  const isFreeText = question.type.includes(QUESTIONS.FREE_TEXT);
  const isChatQuestion = question.type.includes(QUESTIONS.CHAT);

  const shouldUseContextAnalysisForScore = question.type.includes(QUESTIONS.SPEECH_SPONTANEOUS)
    && (Object.keys(answer?.customText?.contextAnalysis?.CEFR ?? {})?.length > 0)

  // remove relevancy, summary and remarks because those are being shown already
  let contextAnalysisKeys = Object.keys(
    answer?.customText?.contextAnalysis ?? {}
  )?.filter(
    (key) => key !== "summary" && key !== "relevancy" && key !== "remarks"
  );
  if (shouldUseContextAnalysisForScore) {
    contextAnalysisKeys = contextAnalysisKeys.filter(key => key.toLowerCase() !== "cefr")
  }

  // To be passed to ScorePills
  const customTextObject = answer?.customText;
  const speechTranscript =
    customTextObject?.body?.speech_score?.transcript;
  const answerScore = isChatQuestion
    ? customTextObject
    : Array.isArray(data?.testcases)
      ? data?.score
      : data?.answer?.score || 0;

  /**
   * Checks the weights of options in an array and returns the highest and lowest weights.
   *
   * Required to assign -> correct, semi-correct and wrong color tags
   * Logic implemented in option mapping -> Component return()
   */
  const weightedQuestionCheck = (options) => {
    if (options.length === 0) return [0, 0];
    let highestWeight = 0,
      lowestWeight = 0;

    for (const option of options) {
      const weight = option.weight;
      if (weight > highestWeight) highestWeight = weight;
      if (weight < lowestWeight) lowestWeight = weight;
    }
    return [highestWeight, lowestWeight];
  };

  const [highestOptionWeight, lowestOptionWeight] =
    weightedQuestionCheck(options);

  /**
   * Using two pointer approach to find word/multiple words
   * Which replace [blank] in questionText
   *
   * @param {object} question
   * @param {object} answer
   * @returns senetenceCompletionText -> Replacing [blank] positions with underlined word/s
   */
  const getSentenceCompletionAnswerText = (question, answer) => {
    const questionText = question.question;
    const answerText = answer.answerText;
    if (!answerText) return <></>;

    // First, getting all the non-blank text
    // Second, replacing non-blank text with "---------" (random string) to split and get the blanks value
    // Then just looping through non-blank array and appending the blanksValue array if value exists
    // * Changed this to handle cases when [blank] is with period or special character. (It also saves nested looping)
    const questionWithoutBlank = questionText.split("[blank]");

    let blanksValue = answerText;
    questionWithoutBlank.forEach((text) => {
      blanksValue = blanksValue.replace(text, "---------");
    });

    blanksValue = blanksValue.split("---------").filter((value) => !!value);

    return questionWithoutBlank.map((text, blankIndex) => (
      <>
        <span className="px-4 weight-600">{text}</span>
        {blanksValue[blankIndex] ? (
          <span className="highlight-word mx-4 py-2 px-10">
            {blanksValue[blankIndex]}
          </span>
        ) : null}
      </>
    ));
  };

  const summary = (answer?.score?.summary || isChatQuestion) && (
    <div className="question-summary">
      <div className="flex items-center justify-between">
        <h2>SUMMARY</h2>
        <div className="relevancy flex items-center justify-start">
          <span className="mr-4">Relevancy:</span>
          <span className="score context-value mr-6">
            {isChatQuestion
              ? answer.customText?.relevancy ?? "--"
              : answer.score?.relevancy ?? "--"}
          </span>
          {
            contextAnalysisKeys?.length > 4
              ? <ScoreBreakdownModal breakdownObj={answer?.customText?.contextAnalysis} />
              : contextAnalysisKeys?.map((contextKey, index) => (
                <>
                  <div className="flex items-center justify-start">
                    <span className="context-key">
                      {capitalizeTitle(contextKey)}:
                    </span>
                    <span className="context-value ml-4">
                      {contextKey === "CEFR Grade"
                        ? answer?.customText?.contextAnalysis[contextKey]
                        : roundOff(answer?.customText?.contextAnalysis[contextKey])}
                    </span>
                  </div>
                  <span
                    className={`contextkey-seperator ml-2 mr-2 ${index === contextAnalysisKeys.length - 1 ? "hide" : ""
                      }`}
                  />
                </>
              ))}
        </div>
      </div>
      {/* Both summary or remark key possible for chat questions */}
      <p>
        {isChatQuestion
          ? answer.customText?.remark || answer.customText?.summary
          : answer.score?.summary}
      </p>
    </div>
  );

  const dialectErrorSummary = isSpeechQuestion && answer?.customText?.dialectError && (
    <div className="question-summary">
      <div className="flex items-center justify-between">
        <h2>SUMMARY</h2>
      </div>
      <p>The audio contains speech in a language different from the expected dialect, so we cannot assess this question.</p>
    </div>
  );

  const remarks = answer?.score?.remarks ? (
    <div className="question-summary">
      <div className="flex justify-between">
        <h2>REMARKS</h2>
        <div className="relevancy remarks flex items-center justify-end">
          {/* show context analysis here in remarks only if summary is not present */}
          {!answer?.score?.summary ? (
            contextAnalysisKeys?.length > 4
              ? <ScoreBreakdownModal breakdownObj={answer?.customText?.contextAnalysis} />
              : contextAnalysisKeys?.map((contextKey, index) => (
                <>
                  <div className="flex items-center justify-start">
                    <span className="context-key">{contextKey}:</span>
                    <span className="context-value ml-4">
                      {contextKey === "CEFR Grade"
                        ? answer?.customText?.contextAnalysis[contextKey]
                        : roundOff(
                          answer?.customText?.contextAnalysis[contextKey]
                        )}
                    </span>
                  </div>
                  <span
                    className={`contextkey-seperator ml-2 mr-2 ${index === contextAnalysisKeys.length - 1 ? "hide" : ""
                      }`}
                  />
                </>
              ))
          ) : (
            <></>
          )}
        </div>
      </div>
      <p>{answer.score.remarks}</p>
    </div>
  ) : (
    <></>
  );

  // Check and show free text as html if received as so
  const isFreeTextAnswerHtml =
    isFreeText && /<\/?[a-z][\s\S]*>/i.test(answer?.answerText);

  return isFreeText || isTypingText ? (
    <div className="mt-20">
      <p className="answer-content-header my-20 weight-600">{`Answer`}</p>
      {isFreeTextAnswerHtml ? (
        <div
          className="answer-content"
          dangerouslySetInnerHTML={{
            __html: answer?.answerText,
          }}
        />
      ) : (
        <pre className="wrap-whitespace answer-content">{answer?.answerText}</pre>
      )}
      <ScorePills
        detailedScoreObject={customTextObject}
        score={answerScore}
        contextAnalysis={customTextObject?.contextAnalysis ?? {}}
      />
      {summary}
      {remarks}
      {!answer?.answerText && nonAttemptedMsg}
    </div>
  ) : isSpeechQuestion ? (
    <div className="mt-20">
      <div className="flex items-center">
        <span className="semibold mr-20">Answer</span>
        <AudioPlayer src={answer?.media?.link} />
      </div>
      {speechTranscript && (
        <div className="audio-transcript mt-20">
          <p className="audio-transcript-title weight-600">Transcript</p>
          <p className="audio-transcript-content">{speechTranscript}</p>
        </div>
      )}
      <ScorePills
        detailedScoreObject={customTextObject}
        score={answerScore}
        contextAnalysis={customTextObject?.contextAnalysis ?? {}}
        shouldUseContextAnalysis={shouldUseContextAnalysisForScore}
      />
      {dialectErrorSummary || summary}
      {!dialectErrorSummary && remarks}
      {!answer?.media?.link && nonAttemptedMsg}
    </div>
  ) : isChatQuestion ? (
    <div className="mt-20">
      <ChatAnswer answer={answer} />
      <ScorePills
        detailedScoreObject={customTextObject}
        score={answerScore}
        contextAnalysis={customTextObject?.contextAnalysis ?? {}}
      />
      {summary}
      {remarks}
      {/* Awaiting design for answerText */}
      {/* {!answer?.answerText && nonAttemptedMsg} */}
    </div>
  ) : isSentenceCompletion ? (
    <div className="mt-20">
      <p className="my-20 answer-content-header">{`Answer`}</p>
      <p className="line-break-any answer-content">
        {getSentenceCompletionAnswerText(question, answer)}
      </p>
      <ScorePills
        detailedScoreObject={customTextObject}
        score={answerScore}
        contextAnalysis={customTextObject?.contextAnalysis ?? {}}
      />
      {summary}
      {remarks}
      {!answer?.answerText && nonAttemptedMsg}
    </div>
  ) : (
    <div className="options flex flex-col">
      {options && options.length > 0 && (
        <div>
          <div className="options-header mt-12 mb-6">Options</div>
          <div
            className={`
            ${checkOptionLength
                ? "flex-col justify-between"
                : "flex justify-start flex-wrap"
              } 
              options-map
            `}
          >
            {options
              .sort((a, b) => a?.order - b?.order)
              .map((option, index) => {
                const optionText = option?.text;
                const [audioSrc, vidSrc, imgSrc, hasMediaSource] =
                  extractMediaDataForOption(option);
                const showMarkedBox = answer?.optionId === option?.id;
                const unWeightedOption =
                  option?.weight === null || option?.weight === undefined;
                let markedBoxCorrect = showMarkedBox && "correct";

                // For weightedQuestion, assign markedBoxCorrect value -> Doubly used in css (semi-correct correspons to yellow tag)
                if (!unWeightedOption && markedBoxCorrect) {
                  if (option?.weight === highestOptionWeight)
                    markedBoxCorrect = "correct";
                  else if (
                    option?.weight === lowestOptionWeight &&
                    lowestOptionWeight <= 0
                  )
                    markedBoxCorrect = "wrong";
                  else markedBoxCorrect = "semi-correct";
                } else {
                  if (showMarkedBox && !option.correct)
                    markedBoxCorrect = false;
                }

                // Check if we should show the most optimal answer pill
                // Show optimal answer pill when: it's a weighted question, a non-optimal answer was selected, and this option has the highest weight
                const isWeightedQuestion = !unWeightedOption && highestOptionWeight !== undefined;
                const selectedOptionWeight = options.find(opt => opt?.id === answer?.optionId)?.weight;
                const shouldShowOptimalAnswer = isWeightedQuestion && 
                  selectedOptionWeight !== undefined &&
                  selectedOptionWeight !== highestOptionWeight &&
                  option?.weight === highestOptionWeight;

                const selectedOptionUI = showMarkedBox && (
                  <span
                    className={`options-marked 
                        ${!markedBoxCorrect ? "wrong" : markedBoxCorrect
                      } ml-10`}
                  >
                    {markedBoxCorrect
                      ? `${unWeightedOption
                        ? "Correctly Answered"
                        : "Selected Answer"
                      }`
                      : "Incorrectly Answered"}
                  </span>
                );

                const optimalAnswerUI = shouldShowOptimalAnswer && (
                  <span
                    className="options-marked correct ml-10"
                  >
                    Optimal Answer
                  </span>
                );

                return (
                  <div
                    className={`options-content flex items-center 
                    ${options.length - 1 > index && !checkOptionLength
                        ? "mr-100"
                        : ""
                      }
                    ${checkOptionLength ? "mt-10" : ""}
                  `}
                  >
                    <span className="mr-10 option-char">
                      {convertOptionListingToChar(index)}
                    </span>
                    {hasMediaSource ? (
                      <div className="flex items-center gap-2">
                        <GetMediaComponent
                          vidSrc={vidSrc}
                          audioSrc={audioSrc}
                          imgSrc={imgSrc}
                        />
                        <span>{selectedOptionUI}{optimalAnswerUI}</span>
                      </div>
                    ) : (
                      <pre
                        className={`options-text wrap-whitespace ${checkOptionLength ? "ws" : ""
                          }`}
                      >
                        {optionText}
                        {selectedOptionUI}
                        {optimalAnswerUI}
                      </pre>
                    )}
                  </div>
                );
              })}
          </div>
          {!answer?.optionId && nonAttemptedMsg}
        </div>
      )}
      <ScorePills
        detailedScoreObject={customTextObject}
        score={answerScore}
        contextAnalysis={customTextObject?.contextAnalysis ?? {}}
      />
      {remarks}
    </div>
  );
};

export const SectionContentAnswer = (props) => {

  const { reportPdfView } = useContext(ReportPdfContext)
  const { activeSection, answerData, plagiarismConfig } = props;

  return (
    <section className={`section-content-answer ${reportPdfView} ? "pdf" : "`}>
      {activeSection === QUIZ_DISPLAY_SECTIONS.MCQ ? (
        <McqSection data={answerData} />
      ) : activeSection === QUIZ_DISPLAY_SECTIONS.PROGRAMMING ? (
        <ProgrammingSection data={answerData} plagiarismConfig={plagiarismConfig} />
      ) : null}
    </section>
  );
};

const nonAttemptedMsg = (
  <p className="mt-10 text-14 semibold text-red">
    Candidate couldn't attempt this question.
  </p>
);
